import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import SectionDesignEditor from '../components/SectionDesignEditor';

const Configs = () => {
    const [config, setConfig] = useState({
        primaryColor: '#9d4edd',
        secondaryColor: '#1e293b',
        backgroundColor: '#1c0042',
        globalFontColor: '#ffffff',
        logoUrl: 'https://www.pcnpublicidad.com/assets/images/logo.png',
        backgroundLogin: '#f3f4f6',
        backgroundImage: '',
        backgroundDesktop: '',
        backgroundMobile: '',
        elementShape: 'rounded-2xl',
        buttonStyles: {
            borderRadius: '12px',
            padding: '12px 24px'
        },
        fontFamily: 'Alliance-Light',
        secondaryFontFamily: 'Alliance-SemiBold',
        tertiaryFontFamily: 'Drina'
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');

    const [pageControls, setPageControls] = useState({
        agenda: true,
        speakers: true,
        streaming: true
    });

    const [pageDesigns, setPageDesigns] = useState({
        home: { enabled: true, bgImage: '', showBrain: false, videos: [] },
        agenda: { enabled: true, bgImage: '', showBrain: false, agendaImageOnly: false, tabActiveGradient: 'bg-[linear-gradient(90deg,#e17c33,#d1583e,#c0344b)]', tabTextColor: '#ffffff', imageAgendas: [], groupColors: [] },
        speakers: { enabled: true, bgImage: '', showBrain: false },
        streaming: { enabled: true, bgImage: '', showBrain: false },
        gallery: { enabled: true, bgImage: '', showBrain: false },
        profile: { enabled: true, bgImage: '', showBrain: false },
        login: { enabled: true, bgImage: '', showBrain: false },
        register: { enabled: true, bgImage: '', showBrain: false },
        welcome: { enabled: true, bgImage: '', showBrain: false },
        thanks: { enabled: true, bgImage: '', showBrain: false },
        certificate: { enabled: true, bgImage: '', showBrain: false },
        questions_private: { enabled: true, bgImage: '', showBrain: false },
        interactiva: { enabled: true, bgImage: '', showBrain: false }
    });

    const [letters, setLetters] = useState({
        welcome: 'Bienvenido {name} {lastName} al evento...',
        welcomeType: 'structured',
        welcomeImage: '',
        structuredData: [],
        thanks: 'Gracias {name} {lastName} por asistir...',
        certificate: 'Certificamos que {name} {lastName} ha participado...',
        certificateData: {
            imageUrl: '',
            x: 50,
            y: 75,
            fontSize: 40,
            color: '#000000',
            bypassAttendance: false
        },
        thanksData: {
            imageUrl: '',
            x: 50,
            y: 50,
            fontSize: 40,
            color: '#ffffff'
        },
        thanksType: 'image',
        thanksStructuredData: []
    });

    const [regFields, setRegFields] = useState({
        identification: true,
        name: true,
        last_name: true,
        email: true,
        city: true,
        country: true,
        signature_by_date: false
    });

    const [customPages, setCustomPages] = useState([]);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const [visualRes, pagesRes, customRes, designsRes, lettersRes, regRes] = await Promise.all([
                    api.get('/configs/visual_identity'),
                    api.get('/configs/page_controls'),
                    api.get('/configs/custom_gallery_pages'),
                    api.get('/configs/page_designs'),
                    api.get('/configs/event_letters'),
                    api.get('/configs/registration_fields')
                ]);
                if (visualRes.data) setConfig(prev => ({ ...prev, ...visualRes.data }));
                if (pagesRes.data) setPageControls(prev => ({ ...prev, ...pagesRes.data }));
                if (customRes.data) setCustomPages(customRes.data);
                if (designsRes.data) setPageDesigns(prev => ({ ...prev, ...designsRes.data }));
                if (lettersRes.data) setLetters(prev => ({
                    ...prev,
                    ...lettersRes.data,
                    certificateData: { ...prev.certificateData, ...(lettersRes.data.certificateData || {}) },
                    thanksData: { ...prev.thanksData, ...(lettersRes.data.thanksData || {}) }
                }));
                if (regRes.data) setRegFields(prev => ({ ...prev, ...regRes.data }));
            } catch (error) {
                console.error("Config not found, using defaults", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setMessage('');
        try {
            await Promise.all([
                api.put('/configs/visual_identity', { value: config }),
                api.put('/configs/page_controls', { value: pageControls }),
                api.put('/configs/custom_gallery_pages', { value: customPages }),
                api.put('/configs/page_designs', { value: pageDesigns }),
                api.put('/configs/event_letters', { value: letters }),
                api.put('/configs/registration_fields', { value: regFields })
            ]);
            setMessage('Configuraciones actualizadas con éxito.');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error saving", error);
            setMessage('Error al guardar la configuración.');
        } finally {
            setIsSaving(false);
        }
    };

    const updatePageDesign = (page, key, value) => {
        setPageDesigns(prev => ({
            ...prev,
            [page]: { ...prev[page], [key]: value }
        }));
    };

    return (
        <div className="max-w-6xl mx-auto animate-fadeIn pb-32">
            <header className="mb-10">
                <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight text-center italic uppercase">Configuración de Experiencia</h1>
                <p className="text-slate-500 mt-2 text-center font-medium">Gestiona la identidad visual global y el diseño específico de cada sección.</p>
            </header>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[50px] shadow-sm">
                    <div className="w-10 h-10 border-4 border-slate-100 border-t-purple-600 rounded-full animate-spin"></div>
                    <p className="mt-4 text-xs font-black uppercase tracking-widest text-slate-400">Sincronizando con el servidor</p>
                </div>
            ) : (
                <div className="space-y-12">
                    {/* Visual Identity Panel */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 overflow-hidden flex flex-col lg:flex-row min-h-[450px]">
                        <div className="lg:w-1/3 p-12 bg-slate-50 flex flex-col items-center justify-center border-r border-slate-100 relative">
                            <div className="absolute top-8 left-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Identidad Global</div>
                            <div className="w-full max-w-[200px] h-32 bg-white rounded-[35px] shadow-xl flex items-center justify-center text-4xl font-black text-white" style={{ backgroundColor: config.primaryColor }}>
                                N
                            </div>
                            <div className="mt-8 text-center">
                                <p className="text-xs font-bold text-slate-900">Muestra de Marca</p>
                            </div>
                        </div>

                        <div className="lg:w-2/3 p-12 space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Color Primario (Tema)</label>
                                    <div className="flex items-center space-x-3">
                                        <input type="color" className="w-12 h-12 rounded-xl border-none cursor-pointer" value={config.primaryColor || '#00595b'} onChange={e => setConfig({ ...config, primaryColor: e.target.value })} />
                                        <input className="bg-slate-100 border-none p-4 rounded-xl font-mono text-xs uppercase" value={config.primaryColor || '#00595b'} onChange={e => setConfig({ ...config, primaryColor: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Color Secundario (Acento / Botones)</label>
                                    <div className="flex items-center space-x-3">
                                        <input type="color" className="w-12 h-12 rounded-xl border-none cursor-pointer" value={config.secondaryColor || '#ff5874'} onChange={e => setConfig({ ...config, secondaryColor: e.target.value })} />
                                        <input className="bg-slate-100 border-none p-4 rounded-xl font-mono text-xs uppercase" value={config.secondaryColor || '#ff5874'} onChange={e => setConfig({ ...config, secondaryColor: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Color Terciario (Insignias / Tabs)</label>
                                    <div className="flex items-center space-x-3">
                                        <input type="color" className="w-12 h-12 rounded-xl border-none cursor-pointer" value={config.tertiaryColor || '#008b8b'} onChange={e => setConfig({ ...config, tertiaryColor: e.target.value })} />
                                        <input className="bg-slate-100 border-none p-4 rounded-xl font-mono text-xs uppercase" value={config.tertiaryColor || '#008b8b'} onChange={e => setConfig({ ...config, tertiaryColor: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Color de Fondo Global</label>
                                    <div className="flex items-center space-x-3">
                                        <input type="color" className="w-12 h-12 rounded-xl border-none cursor-pointer" value={config.backgroundColor || '#00484c'} onChange={e => setConfig({ ...config, backgroundColor: e.target.value })} />
                                        <input className="bg-slate-100 border-none p-4 rounded-xl font-mono text-xs uppercase" value={config.backgroundColor || '#00484c'} onChange={e => setConfig({ ...config, backgroundColor: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Color de Fuente Global</label>
                                    <div className="flex items-center space-x-3">
                                        <input type="color" className="w-12 h-12 rounded-xl border-none cursor-pointer" value={config.globalFontColor || '#ffffff'} onChange={e => setConfig({ ...config, globalFontColor: e.target.value })} />
                                        <input className="bg-slate-100 border-none p-4 rounded-xl font-mono text-xs uppercase" value={config.globalFontColor || '#ffffff'} onChange={e => setConfig({ ...config, globalFontColor: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">URL Logo Corporativo</label>
                                    <input className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm" placeholder="URL .png" value={config.logoUrl} onChange={e => setConfig({ ...config, logoUrl: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Forma de Elementos</label>
                                    <select className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm appearance-none" value={config.elementShape || 'rounded-2xl'} onChange={e => setConfig({ ...config, elementShape: e.target.value })}>
                                        <option value="rounded-none">Cuadrado</option>
                                        <option value="rounded-md">Ligeramente Curvo</option>
                                        <option value="rounded-2xl">Redondeado</option>
                                        <option value="rounded-full">Cápsula (Pill)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Tipografía Principal</label>
                                    <input className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm" placeholder="Ej: Alliance-Light, Inter, Arial" value={config.fontFamily || ''} onChange={e => setConfig({ ...config, fontFamily: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Tipografía Títulos (Secundaria)</label>
                                    <input className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm" placeholder="Ej: Alliance-SemiBold" value={config.secondaryFontFamily || ''} onChange={e => setConfig({ ...config, secondaryFontFamily: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Tipografía Acentos (Terciaria)</label>
                                    <input className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm" placeholder="Ej: Drina, Brush Script" value={config.tertiaryFontFamily || ''} onChange={e => setConfig({ ...config, tertiaryFontFamily: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Imagen de Fondo Desktop Global (PC)</label>
                                    <input
                                        className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm"
                                        placeholder="URL de la imagen de fondo desktop (PC)"
                                        value={config.backgroundDesktop || config.backgroundImage || ''}
                                        onChange={e => setConfig({ ...config, backgroundDesktop: e.target.value, backgroundImage: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">Imagen de Fondo Mobile Global (Móvil)</label>
                                    <input
                                        className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm"
                                        placeholder="URL de la imagen de fondo móvil"
                                        value={config.backgroundMobile || ''}
                                        onChange={e => setConfig({ ...config, backgroundMobile: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">URL Imagen Título Encabezado (Header Title Image)</label>
                                    <input
                                        className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm"
                                        placeholder="URL de la imagen del título del header"
                                        value={config.headerTitleUrl || ''}
                                        onChange={e => setConfig({ ...config, headerTitleUrl: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black tracking-widest uppercase mb-4 pl-1">URL Imagen Pie de Página (Footer Image)</label>
                                    <input
                                        className="w-full bg-slate-100 border-none p-4 rounded-2xl text-sm"
                                        placeholder="URL de la imagen del footer"
                                        value={config.footerImageUrl || config.footerLogoUrl || ''}
                                        onChange={e => setConfig({ ...config, footerImageUrl: e.target.value, footerLogoUrl: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Botones de Confirmación de Asistencia Control Panel */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="mb-8">
                            <h2 className="text-2xl font-black text-slate-900 flex items-center uppercase italic">
                                <span className="mr-4">📌</span> Botones de Confirmación de Asistencia
                            </h2>
                            <p className="text-slate-400 text-sm font-medium mt-1">Activa o desactiva la visibilidad del botón de Pre-confirmación y Confirmación el día del evento de forma independiente.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider">Pre-Confirmación ("Asistiré al Evento")</h4>
                                    <p className="text-xs text-slate-400 mt-1 font-medium">Permite al usuario registrar su intención de asistir previa al evento.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={pageControls.prelogin_attendance_button !== false}
                                        onChange={e => setPageControls({ ...pageControls, prelogin_attendance_button: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>

                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider">Confirmación ("Día del Evento")</h4>
                                    <p className="text-xs text-slate-400 mt-1 font-medium">Permite al usuario confirmar su ingreso presencial el día del evento.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={pageControls.attendance_button !== false}
                                        onChange={e => setPageControls({ ...pageControls, attendance_button: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Page Specific Designs Section */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="mb-12">
                            <h2 className="text-2xl font-black text-slate-900 flex items-center uppercase italic">
                                <span className="mr-4">🎨</span> Diseño y Personalización por Página
                            </h2>
                            <p className="text-slate-400 text-sm font-medium mt-1">Configura el fondo y elementos visuales para cada sección del evento.</p>
                        </div>

                        <div className="space-y-8">
                            {Object.entries(pageDesigns).map(([page, settings]) => (
                                <SectionDesignEditor
                                    key={page}
                                    page={page}
                                    settings={settings}
                                    onUpdate={(key, value) => updatePageDesign(page, key, value)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Event Letters Section */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="mb-12">
                            <h2 className="text-2xl font-black text-slate-900 flex items-center uppercase italic">
                                <span className="mr-4">✉️</span> Textos de Comunicaciones
                            </h2>
                            <p className="text-slate-400 text-sm font-medium mt-1">Configura las cartas de bienvenida, agradecimiento y aspectos logísticos.</p>
                        </div>

                        <div className="space-y-12">
                            {/* Welcome Letter Type Toggle */}
                            <div className="bg-slate-50 p-8 rounded-[40px] border border-slate-100">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 ml-4">Tipo de Bienvenida / Logística</label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {['text', 'image', 'structured'].map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setLetters({ ...letters, welcomeType: type })}
                                            className={`p-6 rounded-[30px] border-2 transition-all flex flex-col items-center gap-3 ${letters.welcomeType === type ? 'bg-purple-600 border-purple-600 text-white shadow-xl shadow-purple-900/20' : 'bg-white border-slate-100 text-slate-400 hover:border-purple-200'}`}
                                        >
                                            <span className="text-2xl">{type === 'text' ? '📝' : type === 'image' ? '🖼️' : '🏗️'}</span>
                                            <span className="text-[10px] font-black uppercase tracking-widest">{type === 'text' ? 'Texto Plano' : type === 'image' ? 'Solo Imagen' : 'Estructurado (Diseño)'}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {letters.welcomeType === 'text' && (
                                <div className="animate-fadeIn">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-4">Carta de Bienvenida (Texto)</label>
                                    <p className="text-[9px] text-slate-400 uppercase mb-4 ml-4">Usa {'{name}'} y {'{lastName}'} para personalizar.</p>
                                    <textarea
                                        className="w-full bg-slate-50 border border-slate-100 p-8 rounded-[40px] text-sm font-medium shadow-inner min-h-[150px] focus:bg-white transition-all"
                                        value={letters.welcome}
                                        onChange={e => setLetters({ ...letters, welcome: e.target.value })}
                                    />
                                </div>
                            )}

                            {letters.welcomeType === 'image' && (
                                <div className="animate-fadeIn">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-4">Carta de Bienvenida (URL de la Imagen)</label>
                                    <input
                                        className="w-full bg-slate-50 border border-slate-100 p-6 rounded-[30px] text-xs font-mono shadow-inner"
                                        placeholder="URL de la imagen (ej: https://...)"
                                        value={letters.welcomeImage || ''}
                                        onChange={e => setLetters({ ...letters, welcomeImage: e.target.value })}
                                    />
                                    {letters.welcomeImage && (
                                        <div className="mt-4 max-w-xs mx-auto rounded-2xl overflow-hidden border border-slate-100 shadow-md">
                                            <img src={letters.welcomeImage} className="w-full h-auto" alt="Vista Previa de Carta" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {letters.welcomeType === 'structured' && (
                                <div className="animate-fadeIn space-y-8">
                                    <div className="flex justify-between items-center px-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Secciones Logísticas</label>
                                        <button
                                            onClick={() => {
                                                const currentData = letters.structuredData || [];
                                                setLetters({ ...letters, structuredData: [...currentData, { title: 'Nueva Sección', icon: '📍', content: '', borderColor: '#9d4edd' }] });
                                            }}
                                            className="text-purple-600 font-black text-[10px] uppercase tracking-widest"
                                        >
                                            + Añadir Sección
                                        </button>
                                    </div>

                                    <div className="grid gap-6">
                                        {(letters.structuredData || []).map((item, idx) => (
                                            <div key={idx} className="bg-slate-50 p-8 rounded-[40px] border border-slate-100 space-y-6 relative group">
                                                <button
                                                    onClick={() => {
                                                        const newData = [...letters.structuredData];
                                                        newData.splice(idx, 1);
                                                        setLetters({ ...letters, structuredData: newData });
                                                    }}
                                                    className="absolute top-6 right-6 w-8 h-8 rounded-full bg-red-50 text-red-400 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all"
                                                >
                                                    ✕
                                                </button>

                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                                    <div>
                                                        <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Icono (Emoji)</p>
                                                        <input
                                                            className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-center text-xl"
                                                            value={item.icon}
                                                            onChange={e => {
                                                                const newData = [...letters.structuredData];
                                                                newData[idx].icon = e.target.value;
                                                                setLetters({ ...letters, structuredData: newData });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Título</p>
                                                        <input
                                                            className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-xs font-bold uppercase"
                                                            value={item.title}
                                                            onChange={e => {
                                                                const newData = [...letters.structuredData];
                                                                newData[idx].title = e.target.value;
                                                                setLetters({ ...letters, structuredData: newData });
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Color Borde</p>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="color"
                                                                className="w-12 h-12 rounded-xl border-none cursor-pointer"
                                                                value={item.borderColor}
                                                                onChange={e => {
                                                                    const newData = [...letters.structuredData];
                                                                    newData[idx].borderColor = e.target.value;
                                                                    setLetters({ ...letters, structuredData: newData });
                                                                }}
                                                            />
                                                            <input
                                                                className="flex-grow bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-mono"
                                                                value={item.borderColor}
                                                                onChange={e => {
                                                                    const newData = [...letters.structuredData];
                                                                    newData[idx].borderColor = e.target.value;
                                                                    setLetters({ ...letters, structuredData: newData });
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Contenido (Usa **texto** para negrita)</p>
                                                    <textarea
                                                        className="w-full bg-white border border-slate-100 p-6 rounded-[30px] text-xs font-medium min-h-[100px]"
                                                        value={item.content}
                                                        onChange={e => {
                                                            const newData = [...letters.structuredData];
                                                            newData[idx].content = e.target.value;
                                                            setLetters({ ...letters, structuredData: newData });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-slate-100 pt-10">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-4">Carta de Agradecimiento</label>
                                    <textarea
                                        className="w-full bg-slate-50 border border-slate-100 p-8 rounded-[40px] text-sm font-medium shadow-inner min-h-[150px] focus:bg-white transition-all"
                                        value={letters.thanks}
                                        onChange={e => setLetters({ ...letters, thanks: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-4">Texto de Certificación (Básico)</label>
                                    <textarea
                                        className="w-full bg-slate-50 border border-slate-100 p-8 rounded-[40px] text-sm font-medium shadow-inner min-h-[150px] focus:bg-white transition-all"
                                        value={letters.certificate}
                                        onChange={e => setLetters({ ...letters, certificate: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* New Certificate Visual Editor */}
                            <div className="border-t border-slate-100 pt-10 animate-fadeIn">
                                <div className="flex justify-between items-center mb-8">
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 uppercase italic">🎓 Editor Visual de Certificado</h3>
                                        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Ubica el nombre del asistente sobre la imagen.</p>
                                    </div>
                                    <button
                                        onClick={() => setLetters({ ...letters, certificateData: { ...letters.certificateData, bypassAttendance: !letters.certificateData?.bypassAttendance } })}
                                        className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${letters.certificateData?.bypassAttendance ? 'bg-orange-500 text-white shadow-lg' : 'bg-slate-100 text-slate-400'}`}
                                    >
                                        {letters.certificateData?.bypassAttendance ? '🔓 Sin Validación QR' : '🔒 Requiere Asistencia (QR)'}
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                                    {/* Settings */}
                                    <div className="space-y-6">
                                        <div>
                                            <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">URL de la Imagen Base</p>
                                            <input
                                                className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-[10px] font-mono"
                                                value={letters.certificateData?.imageUrl || 'No se ha agregado una URL de imagen base'}
                                                onChange={e => setLetters({ ...letters, certificateData: { ...letters.certificateData, imageUrl: e.target.value } })}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Tamaño Fuente (px)</p>
                                                <input
                                                    type="number"
                                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-xs font-bold"
                                                    value={letters.certificateData?.fontSize || 40}
                                                    onChange={e => setLetters({ ...letters, certificateData: { ...letters.certificateData, fontSize: parseInt(e.target.value) } })}
                                                />
                                            </div>
                                            <div>
                                                <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Color Texto</p>
                                                <input
                                                    type="color"
                                                    className="w-full h-12 bg-slate-50 border border-slate-100 p-1 rounded-2xl cursor-pointer"
                                                    value={letters.certificateData?.color || '#000000'}
                                                    onChange={e => setLetters({ ...letters, certificateData: { ...letters.certificateData, color: e.target.value } })}
                                                />
                                            </div>
                                        </div>
                                        <div className="p-6 bg-blue-50 rounded-[30px] border border-blue-100">
                                            <p className="text-[9px] text-blue-400 font-black uppercase tracking-widest mb-2">Instrucciones</p>
                                            <p className="text-[11px] text-blue-600 font-medium leading-relaxed">Haz clic en cualquier parte de la imagen a la derecha para ubicar el centro del nombre del asistente.</p>
                                        </div>
                                    </div>

                                    {/* Visual Editor Canvas */}
                                    <div className="lg:col-span-2">
                                        <div
                                            className="relative bg-slate-200 rounded-[40px] border-4 border-white shadow-2xl overflow-hidden cursor-crosshair"
                                            onClick={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const x = ((e.clientX - rect.left) / rect.width) * 100;
                                                const y = ((e.clientY - rect.top) / rect.height) * 100;
                                                setLetters({ ...letters, certificateData: { ...letters.certificateData, x, y } });
                                            }}
                                        >
                                            <img
                                                src={letters.certificateData?.imageUrl}
                                                alt="Preview"
                                                className="w-full h-auto pointer-events-none"
                                            />
                                            <div
                                                className="absolute pointer-events-none flex items-center justify-center whitespace-nowrap font-black italic uppercase tracking-tighter"
                                                style={{
                                                    left: `${letters.certificateData?.x || 50}%`,
                                                    top: `${letters.certificateData?.y || 50}%`,
                                                    fontSize: `${(letters.certificateData?.fontSize || 40) / 4}px`, // Scaled for preview
                                                    color: letters.certificateData?.color || '#000000',
                                                    transform: 'translate(-50%, -50%)'
                                                }}
                                            >
                                                Nombre del Participante
                                            </div>
                                            {/* Horizontal/Vertical Guides */}
                                            <div className="absolute top-0 bottom-0 border-l border-white/30 pointer-events-none" style={{ left: `${letters.certificateData?.x || 50}%` }}></div>
                                            <div className="absolute left-0 right-0 border-t border-white/30 pointer-events-none" style={{ top: `${letters.certificateData?.y || 50}%` }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Thanks Letter Type Toggle */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="mb-12">
                            <h2 className="text-2xl font-black text-slate-900 flex items-center uppercase italic">
                                <span className="mr-4">🙏</span> Formato de Agradecimiento
                            </h2>
                            <p className="text-slate-400 text-sm font-medium mt-1">Elige entre una imagen dinámica personalizada o un diseño estructurado.</p>
                        </div>

                        <div className="bg-slate-50 p-8 rounded-[40px] border border-slate-100 mb-10">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 ml-4">Tipo de Agradecimiento</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {['image', 'structured'].map(type => (
                                    <button
                                        key={type}
                                        onClick={() => setLetters({ ...letters, thanksType: type })}
                                        className={`p-6 rounded-[30px] border-2 transition-all flex flex-col items-center gap-3 ${letters.thanksType === type ? 'bg-emerald-600 border-emerald-600 text-white shadow-xl shadow-emerald-900/20' : 'bg-white border-slate-100 text-slate-400 hover:border-emerald-200'}`}
                                    >
                                        <span className="text-2xl">{type === 'image' ? '🖼️' : '🏗️'}</span>
                                        <span className="text-[10px] font-black uppercase tracking-widest">{type === 'image' ? 'Imagen Personalizada' : 'Diseño Estructurado (Iconos)'}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {letters.thanksType === 'structured' && (
                            <div className="animate-fadeIn space-y-8">
                                <div className="flex justify-between items-center px-4">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Secciones de Agradecimiento</label>
                                    <button
                                        onClick={() => {
                                            const currentData = letters.thanksStructuredData || [];
                                            setLetters({ ...letters, thanksStructuredData: [...currentData, { title: 'Nueva Sección', icon: '⭐', content: '', borderColor: '#10b981' }] });
                                        }}
                                        className="text-emerald-600 font-black text-[10px] uppercase tracking-widest"
                                    >
                                        + Añadir Sección
                                    </button>
                                </div>

                                <div className="grid gap-6">
                                    {(letters.thanksStructuredData || []).map((item, idx) => (
                                        <div key={idx} className="bg-slate-50 p-8 rounded-[40px] border border-slate-100 space-y-6 relative group">
                                            <button
                                                onClick={() => {
                                                    const newData = [...letters.thanksStructuredData];
                                                    newData.splice(idx, 1);
                                                    setLetters({ ...letters, thanksStructuredData: newData });
                                                }}
                                                className="absolute top-6 right-6 w-8 h-8 rounded-full bg-red-50 text-red-400 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all"
                                            >
                                                ✕
                                            </button>

                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                                <div>
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Icono</p>
                                                    <input
                                                        className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-center text-xl"
                                                        value={item.icon}
                                                        onChange={e => {
                                                            const newData = [...letters.thanksStructuredData];
                                                            newData[idx].icon = e.target.value;
                                                            setLetters({ ...letters, thanksStructuredData: newData });
                                                        }}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Título</p>
                                                    <input
                                                        className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-xs font-bold uppercase"
                                                        value={item.title}
                                                        onChange={e => {
                                                            const newData = [...letters.thanksStructuredData];
                                                            newData[idx].title = e.target.value;
                                                            setLetters({ ...letters, thanksStructuredData: newData });
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Color Borde</p>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="color"
                                                            className="w-12 h-12 rounded-xl border-none cursor-pointer"
                                                            value={item.borderColor}
                                                            onChange={e => {
                                                                const newData = [...letters.thanksStructuredData];
                                                                newData[idx].borderColor = e.target.value;
                                                                setLetters({ ...letters, thanksStructuredData: newData });
                                                            }}
                                                        />
                                                        <input
                                                            className="flex-grow bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-mono"
                                                            value={item.borderColor}
                                                            onChange={e => {
                                                                const newData = [...letters.thanksStructuredData];
                                                                newData[idx].borderColor = e.target.value;
                                                                setLetters({ ...letters, thanksStructuredData: newData });
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Contenido</p>
                                                <textarea
                                                    className="w-full bg-white border border-slate-100 p-6 rounded-[30px] text-xs font-medium min-h-[100px]"
                                                    value={item.content}
                                                    onChange={e => {
                                                        const newData = [...letters.thanksStructuredData];
                                                        newData[idx].content = e.target.value;
                                                        setLetters({ ...letters, thanksStructuredData: newData });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Thanks Letter Visual Editor */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase italic">✨ Editor Visual de Agradecimiento</h3>
                                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Ubica el nombre del asistente en la pantalla final.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            {/* Settings */}
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">URL de la Imagen de Fondo</p>
                                    <input
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-[10px] font-mono"
                                        value={letters.thanksData?.imageUrl || ''}
                                        onChange={e => setLetters({ ...letters, thanksData: { ...letters.thanksData, imageUrl: e.target.value } })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Tamaño Fuente (px)</p>
                                        <input
                                            type="number"
                                            className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl text-xs font-bold"
                                            value={letters.thanksData?.fontSize || 40}
                                            onChange={e => setLetters({ ...letters, thanksData: { ...letters.thanksData, fontSize: parseInt(e.target.value) } })}
                                        />
                                    </div>
                                    <div>
                                        <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">Color Texto</p>
                                        <input
                                            type="color"
                                            className="w-full h-12 bg-slate-50 border border-slate-100 p-1 rounded-2xl cursor-pointer"
                                            value={letters.thanksData?.color || '#ffffff'}
                                            onChange={e => setLetters({ ...letters, thanksData: { ...letters.thanksData, color: e.target.value } })}
                                        />
                                    </div>
                                </div>
                                <div className="p-6 bg-emerald-50 rounded-[30px] border border-emerald-100">
                                    <p className="text-[9px] text-emerald-400 font-black uppercase tracking-widest mb-2">Diseño Premium</p>
                                    <p className="text-[11px] text-emerald-600 font-medium leading-relaxed">Ubica el nombre del asistente sobre una imagen de fondo de alta calidad para la pantalla de agradecimiento.</p>
                                </div>
                            </div>

                            {/* Visual Editor Canvas */}
                            <div className="lg:col-span-2">
                                <div
                                    className="relative bg-slate-200 rounded-[40px] border-4 border-white shadow-2xl overflow-hidden cursor-crosshair"
                                    onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                                        const y = ((e.clientY - rect.top) / rect.height) * 100;
                                        setLetters({ ...letters, thanksData: { ...letters.thanksData, x, y } });
                                    }}
                                >
                                    <img
                                        src={letters.thanksData?.imageUrl || ''}
                                        alt="Preview"
                                        className="w-full h-auto pointer-events-none bg-slate-300"
                                    />
                                    <div
                                        className="absolute pointer-events-none flex items-center justify-center whitespace-nowrap font-black italic uppercase tracking-tighter"
                                        style={{
                                            left: `${letters.thanksData?.x || 50}%`,
                                            top: `${letters.thanksData?.y || 50}%`,
                                            fontSize: `${(letters.thanksData?.fontSize || 40) / 4}px`,
                                            color: letters.thanksData?.color || '#ffffff',
                                            transform: 'translate(-50%, -50%)'
                                        }}
                                    >
                                        Nombre del Participante
                                    </div>
                                    <div className="absolute top-0 bottom-0 border-l border-white/30 pointer-events-none" style={{ left: `${letters.thanksData?.x || 50}%` }}></div>
                                    <div className="absolute left-0 right-0 border-t border-white/30 pointer-events-none" style={{ top: `${letters.thanksData?.y || 50}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Control de Registro y Firma */}
                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="mb-10">
                            <h2 className="text-2xl font-black text-slate-900 flex items-center uppercase italic">
                                <span className="mr-4">✍️</span> Control de Registro y Firma
                            </h2>
                            <p className="text-slate-400 text-sm font-medium mt-1">Configura las reglas para la solicitud de la firma digital de los usuarios.</p>
                        </div>
                        <div className="p-8 bg-slate-50 rounded-[40px] border border-slate-100 flex items-center justify-between">
                            <div className="space-y-1">
                                <h4 className="text-sm font-black text-slate-900 uppercase tracking-wide">Habilitar Firma por Fecha del Evento</h4>
                                <p className="text-slate-400 text-xs font-medium">La firma se deshabilitará hasta que empiece la fecha/hora más cercana de la agenda. Una vez iniciada, se solicitará la firma ("Termine Su Registro") en un modal al ingresar.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setRegFields({ ...regFields, signature_by_date: !regFields.signature_by_date })}
                                className={`w-16 h-8 rounded-full transition-all relative ${regFields.signature_by_date ? 'bg-emerald-500' : 'bg-slate-300'}`}
                            >
                                <span className={`absolute top-1 bg-white w-6 h-6 rounded-full shadow-md transition-all ${regFields.signature_by_date ? 'right-1' : 'left-1'}`} />
                            </button>
                        </div>
                        <div className="p-8 bg-slate-50 rounded-[40px] border border-slate-100 flex items-center justify-between mt-6">
                            <div className="space-y-1">
                                <h4 className="text-sm font-black text-slate-900 uppercase tracking-wide">Habilitar "Representante que lo visita"</h4>
                                <p className="text-slate-400 text-xs font-medium">Muestra el campo para ingresar el nombre del representante en el formulario de registro.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setRegFields({ ...regFields, representative: regFields.representative === false ? true : false })}
                                className={`w-16 h-8 rounded-full transition-all relative ${regFields.representative !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
                            >
                                <span className={`absolute top-1 bg-white w-6 h-6 rounded-full shadow-md transition-all ${regFields.representative !== false ? 'right-1' : 'left-1'}`} />
                            </button>
                        </div>
                        <div className="p-8 bg-slate-50 rounded-[40px] border border-slate-100 mt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="space-y-1">
                                    <h4 className="text-sm font-black text-slate-900 uppercase tracking-wide">Habilitar "Aceptación de Términos y Condiciones"</h4>
                                    <p className="text-slate-400 text-xs font-medium">Muestra el checkbox obligatorio de términos y condiciones en el registro y en el modal de firma.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setRegFields({ ...regFields, terms_accepted: !regFields.terms_accepted })}
                                    className={`w-16 h-8 rounded-full transition-all relative ${regFields.terms_accepted ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                >
                                    <span className={`absolute top-1 bg-white w-6 h-6 rounded-full shadow-md transition-all ${regFields.terms_accepted ? 'right-1' : 'left-1'}`} />
                                </button>
                            </div>
                            {regFields.terms_accepted && (
                                <div className="mt-4 animate-fadeIn">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-2">URL de los Términos y Condiciones</label>
                                    <input
                                        className="w-full bg-white border border-slate-200 p-4 rounded-2xl text-xs shadow-sm font-medium"
                                        placeholder="Ej: https://midominio.com/terminos"
                                        value={regFields.terms_url || ''}
                                        onChange={(e) => setRegFields({ ...regFields, terms_url: e.target.value })}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-[50px] shadow-sm border border-slate-100 p-12">
                        <div className="flex justify-between items-center mb-10">
                            <h2 className="text-2xl font-black text-slate-900 uppercase italic">📸 Galerías de Fotos</h2>
                            <button
                                onClick={() => setCustomPages([...customPages, { id: Date.now(), title: 'Nueva Galería', slug: 'nueva-galeria', icon: '🖼️', type: 'upload', url: '', images: [] }])}
                                className="bg-slate-900 text-white px-8 py-4 rounded-[30px] font-black text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 active:scale-95 transition-all"
                            >
                                + Nueva Galería
                            </button>
                        </div>
                        {/* Custom pages mapping */}
                        <div className="grid grid-cols-1 gap-6">
                            {customPages.map((page, idx) => (
                                <div key={page.id} className="p-10 bg-slate-50 rounded-[40px] border border-slate-100 relative shadow-sm group">
                                    <button
                                        onClick={() => setCustomPages(customPages.filter(p => p.id !== page.id))}
                                        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-red-50 text-red-400 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                                    >
                                        ✕
                                    </button>

                                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                                        <div className="lg:col-span-1 space-y-4">
                                            <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest ml-2">Título e Icono</p>
                                            <div className="flex gap-4">
                                                <input className="w-16 bg-white border border-slate-100 p-4 rounded-2xl text-center text-xl shadow-sm" value={page.icon || '🖼️'} onChange={e => { let n = [...customPages]; n[idx].icon = e.target.value; setCustomPages(n); }} />
                                                <input className="flex-grow bg-white border border-slate-100 p-4 rounded-2xl text-xs font-black uppercase italic tracking-tighter shadow-sm" value={page.title} onChange={e => { let n = [...customPages]; n[idx].title = e.target.value; setCustomPages(n); }} />
                                            </div>
                                            <input className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-[10px] font-medium text-slate-400" value={page.slug} placeholder="slug-de-url" onChange={e => { let n = [...customPages]; n[idx].slug = e.target.value; setCustomPages(n); }} />
                                        </div>

                                        <div className="lg:col-span-3 space-y-6">
                                            <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest ml-2">Tipo de Galería y Configuración</p>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                {[
                                                    { id: 'upload', label: 'Subir Fotos', icon: '📸', desc: 'Gestionado por Staff' },
                                                    { id: 'embed', label: 'Embeber Página', icon: '🌐', desc: 'Iframe 100% ancho' },
                                                    { id: 'redirect', label: 'Redirección', icon: '🔗', desc: 'Link externo (Ventana nueva)' }
                                                ].map(mode => (
                                                    <button
                                                        key={mode.id}
                                                        onClick={() => { let n = [...customPages]; n[idx].type = mode.id; setCustomPages(n); }}
                                                        className={`p-6 rounded-[30px] border-2 transition-all flex flex-col items-center gap-2 ${page.type === mode.id ? 'bg-slate-900 border-slate-900 text-white shadow-xl' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                                    >
                                                        <span className="text-xl">{mode.icon}</span>
                                                        <span className="text-[9px] font-black uppercase tracking-widest">{mode.label}</span>
                                                        <span className="text-[7px] font-bold uppercase opacity-60">{mode.desc}</span>
                                                    </button>
                                                ))}
                                            </div>

                                            {(page.type === 'embed' || page.type === 'redirect') && (
                                                <div className="animate-fadeIn">
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-2 ml-4">URL de Destino (https://...)</p>
                                                    <input
                                                        className="w-full bg-white border border-slate-100 p-4 rounded-2xl text-xs font-mono shadow-inner"
                                                        placeholder="URL externa"
                                                        value={page.url || ''}
                                                        onChange={e => { let n = [...customPages]; n[idx].url = e.target.value; setCustomPages(n); }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[100]">
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className={`px-20 py-6 rounded-[40px] font-black uppercase tracking-[0.3em] text-white shadow-2xl transition-all transform active:scale-95 ${isSaving ? 'bg-slate-400' : 'bg-purple-600 hover:scale-105 hover:bg-purple-500'}`}
                        >
                            {isSaving ? 'Sincronizando...' : 'Guardar Cambios Globales'}
                        </button>
                        {message && (
                            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-max bg-emerald-500 text-white px-10 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest animate-bounce">
                                {message}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Configs;
